import { schema } from 'pltr'

import { appVersion } from './version'
import { getErrorReporterInstance } from './error-reporter-instance'

/**
 * @typedef {import('plottr_world').TemplateEntries} TemplateEntries
 * @typedef {import('pltr').BuiltInTemplate} BuiltInTemplate
 * @typedef {import('pltr').SceneCustomTemplateAttribute} SceneCustomTemplateAttribute
 * @typedef {import('pltr').CharacterCustomTemplateAttribute} CharacterCustomTemplateAttribute
 * @typedef {import('pltr').PlotlineCustomTemplateData} PlotlineCustomTemplateData
 * @typedef {import('pltr').MergeBias} MergeBias
 */

const TEMPLATE_PREFIX = 'templates__'

/**
 * @param {String} string
 * @return {any | Null}
 */
const safeParse = (string) => {
  try {
    return JSON.parse(string)
  } catch (error) {
    return null
  }
}

/**
 * @param {String} templateId
 * @return {String}
 */
const templateKey = (templateId) => {
  return `${TEMPLATE_PREFIX}${templateId}`
}

/**
 * @param {BuiltInTemplate} template
 */
const saveTemplate = (template) => {
  window.localStorage.setItem(templateKey(template.id), JSON.stringify(template))
}

/**
 * @param {String} version
 * @return {String}
 */
const stripAlhpaAndBeta = (version) => {
  return version.replace(/-(alpha|beta).*$/, '')
}

export const seedTemplates = () => {
  const env =
    process.env.NEXT_PUBLIC_NODE_ENV === 'development' ? 'staging' : stripAlhpaAndBeta(appVersion())
  const allTemplatesURL = `/api/built-in-templates/${env}/v2/all-templates`

  return fetch(allTemplatesURL)
    .then((compressedTemplatesResponse) => {
      if (compressedTemplatesResponse.ok) {
        return compressedTemplatesResponse.text()
      } else {
        return compressedTemplatesResponse.text().then((message) => {
          return Promise.reject(new Error('Non 2XX response from server: ' + message))
        })
      }
    })
    .then((compressedTemplatesBundleBase64) => {
      return fetch(`data:text/plain;base64,${compressedTemplatesBundleBase64}`)
    })
    .then((interpretBase64Response) => {
      if (interpretBase64Response.ok) {
        if (interpretBase64Response.body) {
          const decompressionStream = new DecompressionStream('gzip')
          return new Response(interpretBase64Response.body.pipeThrough(decompressionStream)).json()
        } else {
          return Promise.reject(new Error('Invalid response from decoding base 64: no body'))
        }
      } else {
        return Promise.reject(new Error('Error transforming base64 template bundle'))
      }
    })
    .then((templates) => {
      Object.values(templates).forEach(saveTemplate)
      const templatesChangedEvent = new Event('templates-changed')
      document.dispatchEvent(templatesChangedEvent)
    })
    .catch((error) => {
      getErrorReporterInstance().then((errorReporter) => {
        errorReporter.error('Error fetching templates', error)
      })
    })
}

/**
 * @param {String} key
 * @return {boolean}
 */
const isTemplateKey = (key) => {
  return key.startsWith(TEMPLATE_PREFIX)
}

/**
 * @return {Promise<TemplateEntries>}
 */
export const allTemplates = () => {
  return Promise.resolve(
    schema.templates.keepOnlyValidTemplates(
      Object.entries(window.localStorage).reduce((acc, [key, templateString]) => {
        if (isTemplateKey(key)) {
          const template = safeParse(templateString)
          if (template) {
            return [...acc, template]
          } else {
            return acc
          }
        } else {
          return acc
        }
      }, [])
    )
  )
}

/**
 * @typedef {'plotlines' | 'characters' | 'scenes'} TemplateType'
 */

/**
 * @param {TemplateType} type
 */
export const startSaveAsTemplate = (type) => {
  const saveEvent = new Event('start-save-as-template', { bubbles: true, cancelable: false })
  // @ts-ignore
  saveEvent.templateType = type
  document.dispatchEvent(saveEvent)
}

/**
 * @typedef NewTemplateData
 * @property {String} name
 * @property {String} description
 * @property {String} link
 * @property {MergeBias} [bias]
 */

/**
 * @typedef NewCustomTemplatePayload
 * @property {TemplateType} type
 * @property {NewTemplateData} data
 * @property {String} userId
 */

/**
 * @param {NewCustomTemplatePayload} payload
 */
export const messageToSaveNewTemplate = (payload) => {
  const saveEvent = new Event('save-template', { bubbles: true, cancelable: false })
  // @ts-ignore
  saveEvent.payload = payload
  document.dispatchEvent(saveEvent)
}

export const messageToEditTemplate = (_templateId, template) => {
  const editEvent = new Event('edit-template', { bubbles: true, cancelable: false })
  // @ts-ignore
  editEvent.template = template
  document.dispatchEvent(editEvent)
}

export const messageToDeleteTemplate = (templateId) => {
  const deleteEvent = new Event('delete-template', { bubbles: true, cancelable: false })
  // @ts-ignore
  deleteEvent.templateId = templateId
  document.dispatchEvent(deleteEvent)
}
